/**
 * Вкладка «Аккаунты» на `/posts`: блок на сеть, строка — ник, имя (правка по месту, сохранение по потере
 * фокуса), город (`select`: «федеральный» + салоны CRM), «в архив» / «вернуть». Архивные свёрнуты внизу блока.
 * «Добавить аккаунт» — ник + город. CRM недоступна (`crm_error`) — плашка, выбор города неактивен, остальное работает.
 * Ответ POST/PATCH уже несёт свежий список сетей — кладём его в кэш без повторного запроса.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";

import { btn, btnGhost, btnPrimary, Empty, ErrorLine, input, Loading } from "@/components/trends/ui";
import { ApiFailure } from "@/lib/api";
import type { PostCity } from "@/lib/postPlan";
import {
  type AccountNetworkRow,
  type AccountPatch,
  type AccountRow,
  type AccountsResult,
  createAccount,
  loadAccounts,
  patchAccount,
  postsKeys,
} from "@/lib/postsApi";

const CODE_TEXT: Record<string, string> = {
  DUPLICATE: "такой аккаунт уже есть",
  BAD_HANDLE: "пустой ник",
  BAD_NAME: "пустое имя",
  NOT_FOUND: "аккаунт не найден — обновите страницу",
};

function failText(error: unknown): string {
  if (error instanceof ApiFailure) return CODE_TEXT[error.code] ?? error.message;
  return "Не удалось сохранить.";
}

/** Значение `select` города: пусто — федеральный, иначе id салона (имя салона уходит вместе с id). */
function cityOf(value: string, salons: PostCity[]): { crm_location_id: number | null; city_name: string | null } {
  if (value === "") return { crm_location_id: null, city_name: null };
  const id = Number(value);
  return { crm_location_id: id, city_name: salons.find((s) => s.crm_location_id === id)?.name ?? null };
}

function CitySelect({
  value,
  currentName,
  salons,
  disabled,
  onChange,
  label,
}: {
  value: number | null;
  currentName: string | null;
  salons: PostCity[];
  disabled: boolean;
  onChange: (v: string) => void;
  label: string;
}) {
  const known = value === null || salons.some((s) => s.crm_location_id === value);
  return (
    <select
      aria-label={label}
      className={input}
      value={value === null ? "" : String(value)}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">федеральный</option>
      {!known && value !== null ? <option value={String(value)}>{currentName ?? `салон ${value}`}</option> : null}
      {salons.map((s) => (
        <option key={s.crm_location_id} value={String(s.crm_location_id)}>
          {s.name}
        </option>
      ))}
    </select>
  );
}

function Row({
  acc,
  salons,
  crmDown,
  onPatch,
  pending,
  error,
}: {
  acc: AccountRow;
  salons: PostCity[];
  crmDown: boolean;
  onPatch: (b: AccountPatch) => void;
  pending: boolean;
  error: unknown;
}) {
  const [name, setName] = useState(acc.name ?? "");
  const save = () => {
    if (name.trim() !== (acc.name ?? "")) onPatch({ name: name.trim() });
  };
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-44 truncate text-[12.5px] text-fg" title={acc.handle}>
          {acc.handle}
        </span>
        <input
          aria-label={`Имя ${acc.handle}`}
          maxLength={100}
          className={`${input} w-52`}
          value={name}
          placeholder="имя"
          disabled={pending}
          onChange={(e) => setName(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        />
        <CitySelect
          label={`Город ${acc.handle}`}
          value={acc.crm_location_id}
          currentName={acc.city_name}
          salons={salons}
          disabled={crmDown || pending}
          onChange={(v) => onPatch(cityOf(v, salons))}
        />
        <button type="button" className={btnGhost} disabled={pending} onClick={() => onPatch({ archived: !acc.archived })}>
          {acc.archived ? "вернуть" : "в архив"}
        </button>
      </div>
      {error ? <span className="text-[12px] text-err">{failText(error)}</span> : null}
    </div>
  );
}

function AddForm({
  network,
  salons,
  crmDown,
  onAdded,
}: {
  network: string;
  salons: PostCity[];
  crmDown: boolean;
  onAdded: (n: AccountNetworkRow[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [handle, setHandle] = useState("");
  const [city, setCity] = useState("");
  const add = useMutation({
    mutationFn: () => createAccount({ network, handle, ...cityOf(city, salons) }),
    onSuccess: (nets) => {
      onAdded(nets);
      setHandle("");
      setCity("");
      setOpen(false);
    },
  });
  if (!open)
    return (
      <button type="button" className={`${btn} self-start`} onClick={() => setOpen(true)}>
        <Plus size={14} /> Добавить аккаунт
      </button>
    );
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <input aria-label="Ник" maxLength={100} className={`${input} w-52`} placeholder="ник" value={handle} onChange={(e) => setHandle(e.target.value)} />
        <CitySelect
          label="Город нового аккаунта"
          value={city === "" ? null : Number(city)}
          currentName={null}
          salons={salons}
          disabled={crmDown}
          onChange={setCity}
        />
        <button type="button" className={btnPrimary} disabled={add.isPending} onClick={() => add.mutate()}>
          Добавить
        </button>
        <button type="button" className={btnGhost} onClick={() => {
            add.reset();
            setOpen(false);
          }}>
          Отмена
        </button>
      </div>
      {add.isError ? <span className="text-[12px] text-err">{failText(add.error)}</span> : null}
    </div>
  );
}

export function AccountsTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: postsKeys.accounts, queryFn: loadAccounts });
  const [showArchive, setShowArchive] = useState<Record<string, boolean>>({});
  const put = (networks: AccountNetworkRow[]) =>
    qc.setQueryData<AccountsResult>(postsKeys.accounts, (old) => (old ? { ...old, networks } : old));
  const patch = useMutation({
    mutationFn: (v: { id: number; body: AccountPatch }) => patchAccount(v.id, v.body),
    onSuccess: put,
  });

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorLine error={q.error} />;
  const { networks, salons, crm_error } = q.data;
  const crmDown = crm_error !== null;
  const failedId = patch.isError ? patch.variables.id : undefined;

  return (
    <div className="flex flex-col gap-4">
      {crmDown ? (
        <div className="rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-3 py-2 text-[12.5px] text-fg-muted">
          CRM недоступна ({crm_error}): список городов не загружен, выбор города отключён.
        </div>
      ) : null}
      {networks.length === 0 ? <Empty>Сетей нет.</Empty> : null}
      {networks.map((n) => {
        const live = n.accounts.filter((a) => !a.archived);
        const archived = n.accounts.filter((a) => a.archived);
        const row = (a: AccountRow) => (
          <Row
            key={`${a.id}:${a.name ?? ""}`}
            acc={a}
            salons={salons}
            crmDown={crmDown}
            pending={patch.isPending && patch.variables.id === a.id}
            error={failedId === a.id ? patch.error : null}
            onPatch={(body) => patch.mutate({ id: a.id, body })}
          />
        );
        return (
          <section key={n.code} className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-border p-3">
            <h2 className="text-[13.5px] font-medium text-fg">{n.name}</h2>
            {live.length === 0 ? <span className="text-[12.5px] text-fg-faint">Аккаунтов нет.</span> : live.map(row)}
            {archived.length > 0 ? (
              <>
                <button
                  type="button"
                  className={`${btnGhost} self-start`}
                  onClick={() => setShowArchive((s) => ({ ...s, [n.code]: !s[n.code] }))}
                >
                  {showArchive[n.code] ? "скрыть архив" : `архив (${archived.length})`}
                </button>
                {showArchive[n.code] ? archived.map(row) : null}
              </>
            ) : null}
            <AddForm network={n.code} salons={salons} crmDown={crmDown} onAdded={put} />
          </section>
        );
      })}
    </div>
  );
}
